import {SyncStatus} from '../offline/entity.js';
import {RECIPE_SAVE_ENTITY_TYPE} from './recipe-save-store.js';

export function createRecipeSaveSync({repository,store,remote}={}){
  if(!repository?.put||!repository?.remove)throw new TypeError('repository is required');
  if(!store?.queue?.listReady||!store?.queue?.markAttempt||!store?.queue?.markFailure||!store?.replaceFromRemote){
    throw new TypeError('Recipe save store with retry-aware queue is required');
  }
  if(!remote?.list||!remote?.upsert||!remote?.remove)throw new TypeError('Recipe save remote is required');

  async function push(){
    const pending=(await store.queue.listReady())
      .filter(row=>row.entityType===RECIPE_SAVE_ENTITY_TYPE);
    const results=[];
    for(const row of pending){
      const attempted=await store.queue.markAttempt(RECIPE_SAVE_ENTITY_TYPE,row);
      try{
        if(attempted.syncStatus===SyncStatus.PENDING_DELETE){
          await remote.remove(attempted);
          // Keep a synced tombstone until a later pull confirms the remote row
          // is actually absent. This prevents eventual-consistency/stale reads
          // from resurrecting a user deletion immediately after remote ACK.
          await repository.put(RECIPE_SAVE_ENTITY_TYPE,{
            ...attempted,
            syncStatus:SyncStatus.SYNCED,
            syncAttempts:0,
            lastSyncFailureAt:null,
            lastSyncErrorCode:null,
            nextSyncAttemptAt:null,
            conflict:null
          });
          results.push(Object.freeze({status:'deleted',id:attempted.id}));
          continue;
        }
        const saved=await remote.upsert(attempted);
        const synced={
          ...attempted,
          ...saved,
          id:attempted.id,
          userId:attempted.userId,
          syncStatus:SyncStatus.SYNCED,
          serverVersion:Number(saved.serverVersion)||Number(attempted.serverVersion)||1,
          syncAttempts:0,
          deletedAt:null,
          lastSyncFailureAt:null,
          lastSyncErrorCode:null,
          nextSyncAttemptAt:null,
          conflict:null
        };
        await repository.put(RECIPE_SAVE_ENTITY_TYPE,synced);
        results.push(Object.freeze({status:'synced',id:attempted.id}));
      }catch(error){
        const failed=await store.queue.markFailure(
          RECIPE_SAVE_ENTITY_TYPE,
          attempted,
          String(error?.code||error?.name||'RECIPE_SAVE_SYNC_FAILED')
        );
        results.push(Object.freeze({status:'retry',id:attempted.id,entity:failed}));
      }
    }
    return Object.freeze(results);
  }

  async function pull(){
    const rows=await remote.list();
    return store.replaceFromRemote(rows);
  }

  async function sync(){
    const pushed=await push();
    const current=await pull();
    return Object.freeze({pushed,current});
  }

  return Object.freeze({push,pull,sync});
}
